import type { DayHours, OpeningHours } from '~models/station.model';

export enum StationStatus {
  OPEN = 'open',
  CLOSED = 'closed',
  CLOSES_SOON = 'closes-soon',
}

/** Default threshold for "closes soon" warning in minutes */
export const CLOSES_SOON_THRESHOLD_MINUTES = 30;

/** Days of the week mapping (Sunday = 0 in JS Date) */
const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

type DayKey = (typeof DAY_KEYS)[number];

/**
 * Parses a time string (HH:MM) into total minutes since midnight
 */
const parseTimeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);

  return hours * 60 + minutes;
};

/**
 * Gets the current day key
 */
const getCurrentDayKey = (date: Date = new Date()): DayKey => {
  return DAY_KEYS[date.getDay()];
};

/**
 * Gets the previous day key
 */
const getPreviousDayKey = (date: Date = new Date()): DayKey => {
  const previousDay = (date.getDay() + 6) % 7; // Sunday(0)->Saturday(6), Monday(1)->Sunday(0), etc.

  return DAY_KEYS[previousDay];
};

/**
 * Gets the current time as minutes since midnight
 */
const getCurrentTimeInMinutes = (date: Date = new Date()): number => {
  return date.getHours() * 60 + date.getMinutes();
};

/**
 * Checks if current time is within opening hours, handling midnight crossings
 *
 * @param currentMinutes - Current time as minutes since midnight
 * @param openMinutes - Opening time as minutes since midnight
 * @param closeMinutes - Closing time as minutes since midnight
 */
const isWithinHours = (currentMinutes: number, openMinutes: number, closeMinutes: number): boolean => {
  // Normal case: opening and closing on same day (e.g., 09:00 - 23:00)
  if (openMinutes < closeMinutes) {
    return currentMinutes >= openMinutes && currentMinutes < closeMinutes;
  }

  // Midnight crossing case: closing time is next day (e.g., 22:00 - 02:00)
  // Open if current time is after opening OR before closing
  return currentMinutes >= openMinutes || currentMinutes < closeMinutes;
};

/**
 * Calculates minutes until closing, handling midnight crossings
 *
 * @param currentMinutes - Current time as minutes since midnight
 * @param closeMinutes - Closing time as minutes since midnight
 * @param openMinutes - Opening time as minutes since midnight
 */
const getMinutesUntilClose = (currentMinutes: number, closeMinutes: number, openMinutes: number): number => {
  if (openMinutes < closeMinutes) {
    return closeMinutes - currentMinutes;
  }

  // Midnight crossing case
  if (currentMinutes >= openMinutes) {
    // After opening (before midnight): time to midnight + time after midnight until close
    return 24 * 60 - currentMinutes + closeMinutes;
  }

  // After midnight (before closing): remaining time until close
  return closeMinutes - currentMinutes;
};

/**
 * Calculates the current status of a station based on its opening hours
 *
 * @param openingHours - The station opening hours configuration
 * @param closesSoonThresholdMinutes - Minutes before closing to show "closes soon"
 * @param currentDate - Optional date for testing
 * @returns StationStatus
 */
export const getStationStatus = (
  openingHours: OpeningHours | undefined,
  closesSoonThresholdMinutes: number = CLOSES_SOON_THRESHOLD_MINUTES,
  currentDate: Date = new Date(),
): StationStatus => {
  if (!openingHours) {
    return StationStatus.CLOSED;
  }

  if (openingHours.alwaysOpen === true) {
    return StationStatus.OPEN;
  }

  const currentMinutes = getCurrentTimeInMinutes(currentDate);
  const dayKey = getCurrentDayKey(currentDate);
  const todayHours: DayHours | undefined = openingHours[dayKey];

  if (todayHours) {
    const openMinutes = parseTimeToMinutes(todayHours.open);
    const closeMinutes = parseTimeToMinutes(todayHours.close);

    if (isWithinHours(currentMinutes, openMinutes, closeMinutes)) {
      const minutesUntilClose = getMinutesUntilClose(currentMinutes, closeMinutes, openMinutes);

      if (minutesUntilClose <= closesSoonThresholdMinutes) {
        return StationStatus.CLOSES_SOON;
      }

      return StationStatus.OPEN;
    }
  }

  // Check if previous day's midnight-crossing shift extends into current time
  const previousDayKey = getPreviousDayKey(currentDate);
  const previousDayHours: DayHours | undefined = openingHours[previousDayKey];

  if (previousDayHours) {
    const prevOpenMinutes = parseTimeToMinutes(previousDayHours.open);
    const prevCloseMinutes = parseTimeToMinutes(previousDayHours.close);

    // Only check if previous day crosses midnight (close < open)
    if (prevCloseMinutes < prevOpenMinutes && currentMinutes < prevCloseMinutes) {
      const minutesUntilClose = prevCloseMinutes - currentMinutes;

      if (minutesUntilClose <= closesSoonThresholdMinutes) {
        return StationStatus.CLOSES_SOON;
      }

      return StationStatus.OPEN;
    }
  }

  return StationStatus.CLOSED;
};

export const isStationOpen = (openingHours: OpeningHours | undefined, currentDate: Date = new Date()): boolean => {
  const status = getStationStatus(openingHours, CLOSES_SOON_THRESHOLD_MINUTES, currentDate);

  return status !== StationStatus.CLOSED;
};
